import { useState, useCallback } from "react";
import {
  useDeleteSafetyCheck,
  type DeleteSafetyResult,
} from "./useDeleteSafetyCheck";
import { useArchiveDoc } from "./useArchiveDoc";

export type SmartDeleteDialogState =
  | "idle"
  | "checking"
  | "smart-archive"
  | "choice"
  | "permanent-delete";

const FALLBACK_SAFETY: DeleteSafetyResult = {
  can_delete: true,
  has_disabled_field: true,
  linked_count: 0,
  linked_doctypes: [],
  linked_summary: "",
  variant_count: 0,
};

/**
 * Orchestrates the smart delete flow:
 * 1. User clicks Delete → calls initiateDelete()
 * 2. Safety check runs → decides which dialog to show
 * 3. Dialog actions (archive / permanent delete) are handled by returned mutations
 *
 * Does NOT contain useNavigate — navigation is the caller's responsibility.
 */
export function useSmartDeleteFlow(doctype: string, name: string) {
  const [dialogState, setDialogState] =
    useState<SmartDeleteDialogState>("idle");
  const [safetyData, setSafetyData] = useState<DeleteSafetyResult | null>(null);

  const safetyCheck = useDeleteSafetyCheck();
  const archiveMutation = useArchiveDoc(doctype);

  const initiateDelete = useCallback(() => {
    setDialogState("checking");
    safetyCheck.mutate(
      { doctype, name },
      {
        onSuccess: (data) => {
          setSafetyData(data);
          if (!data.can_delete) {
            setDialogState("smart-archive");
          } else {
            setDialogState("choice");
          }
        },
        onError: () => {
          // Fallback: if safety check fails, show choice dialog
          setSafetyData(FALLBACK_SAFETY);
          setDialogState("choice");
        },
      },
    );
  }, [doctype, name, safetyCheck]);

  const closeDialogs = useCallback(() => {
    setDialogState("idle");
    setSafetyData(null);
  }, []);

  return {
    dialogState,
    setDialogState,
    safetyData,
    initiateDelete,
    closeDialogs,
    archiveMutation,
    isChecking: safetyCheck.isPending,
  };
}
